import { useState, useEffect, useMemo, useCallback } from 'react';
import { Property } from '../types';
import { PropertyHealthAudit, InventoryHealthStats, HealthRuleId } from '../types/inventoryHealth';
import { get183150ActivePropertiesApi, savePropertyApi } from '../services/adminPropertyApiClient';
import { auditProperty } from '../utils/health/auditEngine';
import { calculateHealthStats, extractUniqueOwners } from '../utils/health/healthStatsCalculator';
import { exportHealthAuditsToCsv } from '../utils/health/healthCsvExporter';

export const useInventoryHealth = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [selectedRule, setSelectedRule] = useState<HealthRuleId | 'all' | 'healthy_only' | 'critical_only' | 'warning_only'>('all');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedOwner, setSelectedOwner] = useState('');

  const loadActiveProperties = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await get183150ActivePropertiesApi();
      setProperties(items);
    } catch (err: any) {
      console.error('Error fetching active 183150 properties:', err);
      setError(err?.message || 'Error al obtener inventario activo');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActiveProperties();
  }, [loadActiveProperties]);

  const audits = useMemo(() => properties.map((p) => auditProperty(p)), [properties]);
  const stats: InventoryHealthStats = useMemo(() => calculateHealthStats(audits), [audits]);
  const owners = useMemo(() => extractUniqueOwners(audits), [audits]);

  const filteredAudits = useMemo(() => {
    return audits.filter((a) => {
      const p = a.property;
      if (selectedRule === 'healthy_only' && !a.isHealthy) return false;
      if (selectedRule === 'critical_only' && !a.hasCritical) return false;
      if (selectedRule === 'warning_only' && (a.isHealthy || a.hasCritical)) return false;
      if (
        selectedRule !== 'all' &&
        selectedRule !== 'healthy_only' &&
        selectedRule !== 'critical_only' &&
        selectedRule !== 'warning_only' &&
        !a.issues.some((i) => i.ruleId === selectedRule)
      ) {
        return false;
      }
      if (selectedCity && (p.ciudad || '').toLowerCase() !== selectedCity.toLowerCase()) return false;
      if (selectedOwner && (p.owner || '').trim().toLowerCase() !== selectedOwner.trim().toLowerCase()) return false;
      if (search) {
        const term = search.toLowerCase();
        const code = (p.codigo || '').toLowerCase();
        const brp = (p.codigo_brp || '').toLowerCase();
        const dir = (p.direccion || '').toLowerCase();
        const city = (p.ciudad || '').toLowerCase();
        const owner = (p.owner || '').toLowerCase();
        if (!code.includes(term) && !brp.includes(term) && !dir.includes(term) && !city.includes(term) && !owner.includes(term)) {
          return false;
        }
      }
      return true;
    });
  }, [audits, selectedRule, selectedCity, selectedOwner, search]);

  const quickSave = async (id: string, updates: Partial<Property>): Promise<boolean> => {
    try {
      await savePropertyApi(id, updates);
      setProperties((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates } : p)));
      return true;
    } catch (e) {
      console.error('Quick save failed:', e);
      return false;
    }
  };

  return {
    audits: filteredAudits,
    allAudits: audits,
    stats,
    owners,
    loading,
    error,
    search,
    setSearch,
    selectedRule,
    setSelectedRule,
    selectedCity,
    setSelectedCity,
    selectedOwner,
    setSelectedOwner,
    refresh: loadActiveProperties,
    quickSave,
    exportCsv: () => exportHealthAuditsToCsv(filteredAudits),
  };
};
